<template>
  <div>
    <SectionSeparatorWrapper v-if="showProductInfo" :headerText="referenceData.additionalInformationConfirmationHeading.informationText" :separator="false">
      <div class="new-product-card fr-view">
        <section class="product-info">
          <h2>{{ selectedProducts.recommendationName }}</h2>
          <ul>
            <li v-html="referenceData.additionalInformationConfirmationPremiumsInformationText.informationText"></li>
            <li>
              <p>Cover for a {{ summaryCoverComposition(selectedProducts.composition) }}</p>
            </li>
            <li>
              <p>Your cover will start on {{ reverseFormatFormDate(changeCoverData.step1.startDate) }}</p>
            </li>
            <!-- prettier-ignore -->
            <li v-if="currentCoverStartDate !== getCurrentDate()">
              <p>Your previous premium was {{ convertToCurrencyString(currentPremium) }} due {{ paymentFrequency.toLowerCase() }}</p>
            </li>
            <li v-if="selectedProducts.coverType !== CoverTypeValue.EXTRAS_ONLY" v-html="referenceData.additionalInformationConfirmationInformationText.informationText"></li>
          </ul>
        </section>
        <section class="price-info">
          <h3>Your new ongoing premium</h3>
          <h2 class="new-price">
            {{ convertToCurrencyString(selectedProducts.price) }}
            /
            {{ paymentFrequency }}
          </h2>
          <div v-if="adjustments && adjustments.discounts && adjustments.discounts.length > 0" class="discounts-loadings">
            <p id="disc-load-subheading">Discounts applied</p>
            <p class="description-small">
              <a href="https://www.defencehealth.com.au/Health-Insurance/Choosing-Defence-Health/Why-Choose-Us">Learn more</a>
              about the discounts we offer to our Defence community.
            </p>
            <ul v-for="discount in adjustments.discounts" :key="`${discount.category}-${discount.percentage}`">
              <li class="discounts-loadings-list">
                <span class="discount-category">{{ stringifyDiscountCategory(discount.category) }}</span>
                <!-- Show 3 decimal places only for Rebate type discount, otherwise whole integers -->
                <!-- <span v-if="isRebateCategory(category)" class="discount-percentage">{{ formatPercentageString(percentage, 3) }}</span>
                <span v-else class="discount-percentage">{{ formatPercentageString(percentage, 0) }}</span> -->
              </li>
            </ul>
          </div>
          <div v-if="adjustments && adjustments.loadings && adjustments.loadings.length > 0" class="discounts-loadings fr-view">
            <p id="disc-load-subheading">Loadings applied</p>
            <div v-if="informationReferenceData" class="description-small" v-html="informationReferenceData.confirmationLoadingsInformationText.informationText"></div>
            <ul>
              <li v-for="loading in adjustments.loadings" :key="`${loading.category}`" class="discounts-loadings-list">
                <span class="discount-category">{{ stringifyDiscountCategory(loading.category) }}</span>
                <span v-if="isLhcSurchargeCategory(loading.category)" class="discount-percentage">{{ formatPercentageString(loading.percentage, 0) }}</span>
              </li>
            </ul>
          </div>
        </section>
        <div v-if="referenceData">
          <div class="declaration-content">
            <div class="form-section">
              <div class="field checkbox">
                <input type="checkbox" id="declareInfo" v-model="isInfoDeclared" @change="isInfoDeclaredDirty = true" @keyup.enter.space="isInfoDeclared = !isInfoDeclared" />
                <label for="declareInfo" v-html="referenceData.additionalInformationConfirmationUserAcceptanceText.informationText"></label>
                <label v-if="isInfoDeclaredDirty && !isInfoDeclared" for="declareInfo" class="error">Please include a response for the acceptance of terms and conditions.</label>
              </div>
            </div>
            <div class="form-page-nav">
              <section class="form-buttons">
                <button class="secondary" @click="emitBackButtonPressed">Back</button>
                <button :disabled="!isInfoDeclared" @click="emitChangeCover">Change my cover</button>
              </section>
            </div>
          </div>
        </div>
      </div>
    </SectionSeparatorWrapper>
  </div>
</template>

<script>
import { CoverTypeValue, DiscountCategoriesJSON } from '@general/js/enums';
import { convertToCurrencyString, formatPercentageString, getCurrentDate, reverseFormatFormDate, stringifyDiscountCategory, summaryCoverComposition } from '@general/js/utils';
import { SectionSeparatorWrapper } from '@/components';
import CaptureInformationMixins from '../CaptureInformationMixins';
import ChangeCoverMixins from '../ChangeCoverMixins';

export default {
  name: 'Confirmation',

  mixins: [CaptureInformationMixins, ChangeCoverMixins],

  components: {
    SectionSeparatorWrapper,
  },

  props: {
    informationReferenceData: {
      type: Object,
      required: false,
    },
  },

  data() {
    return {
      isInfoDeclared: null,
      isInfoDeclaredDirty: false,
      CoverTypeValue,
      adjustments: {},
    };
  },

  computed: {
    showProductInfo() {
      if (this.changeCoverData.selectedProducts.coverType === CoverTypeValue.EXTRAS_ONLY) {
        return this.selectedProducts && this.changeCoverData;
      }
      return this.selectedProducts && this.selectedExcess && this.changeCoverData;
    },

    showDiscountsAndLoadings() {
      return this.selectedProducts?.discountsAndLoadings && Object.keys(this.selectedProducts?.discountsAndLoadings).length !== 0;
    },
  },

  mounted() {
    this.adjustments = this.getDiscountsAndLoadingsList(this.selectedProducts?.discountsAndLoadings);
  },

  methods: {
    emitBackButtonPressed() {
      this.$emit('backButtonPressed');
    },
    emitChangeCover() {
      this.$emit('changeCoverSubmitted');
    },
    convertToCurrencyString,
    formatPercentageString,
    getCurrentDate,
    reverseFormatFormDate,
    stringifyDiscountCategory,
    summaryCoverComposition,
    isRebateCategory(category) {
      return category === DiscountCategoriesJSON.REBATE_DISCOUNT;
    },
    isLhcSurchargeCategory(category) {
      return category === DiscountCategoriesJSON.LHC_SURCHARGE;
    },
  },
};
</script>

<style lang="scss" scoped>
.new-product-card {
  @include flex-col;

  background-color: $WHITE;
  border-radius: 1rem;
  box-shadow: 0px 2px 6px 0px rgba(22, 22, 29, 0.06), 0px 2px 4px 0px rgba(22, 22, 29, 0.02);
  margin: 4rem 0;
  padding: 2rem;

  .product-info {
    h2 {
      margin-bottom: 1.5rem;
    }
    margin-bottom: 1.5rem;
  }

  .price-info {
    .new-price {
      color: $PRIMARY_GREEN_100;
    }
    h3 {
      margin-bottom: 1.5rem;
    }
  }
}

.form-section {
  margin-bottom: rem(40);

  & > .form-page-nav {
    display: block;
    margin-top: 0;

    section {
      display: grid;
      column-gap: rem(15);
      row-gap: rem(15);
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto;

      button:not(.secondary) {
        justify-self: end;
      }

      // When Join Defence Health button break into tow lines
      @media screen and (max-width: 885px) {
        grid-template-columns: auto;
        grid-template-rows: 1fr 1fr;
        row-gap: rem(25);

        > :last-child {
          grid-column: 1;
        }

        button {
          max-width: none;
        }

        .secondary {
          grid-row-start: 2;
        }
      }
    }
  }

  input[type='checkbox'] + label {
    &::before,
    &::after {
      top: rem(10);
    }
  }
}

.full-width {
  background: $GREY_50;
  position: relative;

  &::before {
    content: '';
    background-color: $GREY_50;
    position: absolute;
    height: 100%;
    width: 100vw;
    left: calc(-50vw + 550px - 305px);
    z-index: -1;

    @include desktop {
      height: calc(100% + 20px);
    }

    @include tablet {
      width: calc(100% / 3 * 4 + 68px);
      left: calc(-48px - 100% / 3);
    }

    @media screen and (min-width: 1140px) and (max-width: $TABLET_MAX) {
      width: 100vw;
      left: calc(-50vw + 550px - 265px - 28px);
    }

    @include mobile {
      width: calc(100% + 40px);
      left: rem(-20);
    }
  }
}

.field.checkbox {
  margin-bottom: 0;

  :deep(p:last-of-type) {
    margin-bottom: 0;
  }

  @include mobile {
    padding-top: rem(25);
  }
}

.declaration-content {
  padding-top: rem(30);

  @include mobile {
    padding-top: 0;
  }

  .form-field {
    margin-bottom: 0;
  }

  .form-field:last-of-type {
    @include desktop {
      margin-bottom: rem(20);
    }

    @include tablet {
      margin-bottom: 0;
    }

    :deep(.form-section) {
      @include tablet {
        margin-bottom: rem(60);
      }

      @include mobile {
        margin-bottom: rem(50);
      }
    }
  }
}

.discounts-loadings {
  margin-top: rem(24);

  #disc-load-subheading {
    font-weight: $BOLD;
    font-size: 16px;
    margin-bottom: rem(4);
  }

  .description-small {
    margin-bottom: 1rem;
    font-size: 14px;
  }

  .discounts-loadings-list {
    display: flex;
    align-items: baseline;
  }

  .discount-category {
    text-align: left;
    margin-left: 5px;
  }

  .discount-percentage {
    font-weight: $BOLD;
    margin-left: auto;
  }

  ul > li::before {
    content: '';
    background-image: url(/assets/public/images/bullet-icon.svg);
    background-size: contain;
    background-repeat: no-repeat;
    vertical-align: middle;
    width: 15px;
    height: 15px;
    margin-top: 4px;
  }

  ul {
    max-width: 50%;
  }
}
</style>
